import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { AlertTriangle } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useAuth } from '@/features/auth'
import {
  useAlerts,
  useAlertBadgeCount,
  useActOnAlert,
  type AlertFilters,
  type ClinicalAlert,
} from '@/features/insights/hooks/use-insights'
import { InsightCard } from '@/features/insights/components/InsightCard'
import { InsightModal } from '@/features/insights/components/InsightModal'
import { InsightsFiltersComponent } from '@/features/insights/components/InsightsFilters'
import { DismissDropdown } from '@/features/insights/components/DismissDropdown'
import { RecordForm } from '@/features/patients/components/RecordForm'
import { NewAppointmentModal } from '@/features/scheduling/components/NewAppointmentModal'

export const Route = createFileRoute('/_authenticated/insights')({
  component: AlertsPage,
})

function AlertsPage() {
  useDocumentTitle('Alertas')
  const { currentOrganization } = useAuth()
  const [filters, setFilters] = useState<AlertFilters>({ status: 'active' })
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(null)
  const [dismissingAlert, setDismissingAlert] = useState<ClinicalAlert | null>(null)
  const [evolutionAlert, setEvolutionAlert] = useState<ClinicalAlert | null>(null)
  const [schedulingAlert, setSchedulingAlert] = useState<ClinicalAlert | null>(null)

  const { data: alerts, isLoading } = useAlerts(filters)
  const { data: badgeCount } = useAlertBadgeCount()
  const actOnAlert = useActOnAlert()

  // Hide for assistants
  if (currentOrganization?.userRole === 'assistant') {
    return (
      <MainLayout title="Alertas">
        <div className="card p-12 text-center">
          <p className="text-gray-600">Você não tem acesso a alertas clínicos.</p>
        </div>
      </MainLayout>
    )
  }

  const handleCreateEvolution = (alert: ClinicalAlert) => {
    setEvolutionAlert(alert)
  }

  const handleEvolutionSaved = () => {
    if (evolutionAlert) {
      actOnAlert.mutate({
        insightId: evolutionAlert.id,
        actionType: 'evolution_created',
      })
      setEvolutionAlert(null)
    }
  }

  const handleScheduleSession = (alert: ClinicalAlert) => {
    // TODO: NewAppointmentModal needs onSuccess callback to mark alert as resolved
    // only after the appointment is actually created. For now, open the modal
    // and the user can dismiss the alert separately after scheduling.
    setSchedulingAlert(alert)
  }

  const handleDismiss = (alert: ClinicalAlert) => {
    setDismissingAlert(alert)
  }

  return (
    <MainLayout title="Alertas" subtitle="Alertas clínicos gerados a partir dos relatos de familiares">
      <div className="space-y-6">
        {/* Header com contador */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-orange-50 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">
                {alerts?.length ?? 0} alerta{(alerts?.length ?? 0) !== 1 ? 's' : ''}
                {filters.status === 'active' ? ' ativo' : ''}
                {(alerts?.length ?? 0) !== 1 && filters.status === 'active' ? 's' : ''}
              </p>
            </div>
          </div>
          {(badgeCount ?? 0) > 0 && filters.status !== 'active' && (
            <div className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
              {badgeCount} ativo{(badgeCount ?? 0) !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        {/* Filtros */}
        <div className="card p-4">
          <InsightsFiltersComponent filters={filters} onFiltersChange={setFilters} />
        </div>

        {/* Lista de alertas */}
        {isLoading ? (
          <AlertsSkeleton />
        ) : alerts && alerts.length > 0 ? (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <InsightCard
                key={alert.id}
                alert={alert}
                onViewDetails={(id) => setSelectedAlertId(id)}
                onCreateEvolution={handleCreateEvolution}
                onScheduleSession={handleScheduleSession}
                onDismiss={handleDismiss}
                isActing={actOnAlert.isPending}
              />
            ))}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-full mb-4">
              <AlertTriangle className="h-6 w-6 text-green-400" />
            </div>
            <p className="text-gray-600 font-medium">
              {filters.status === 'active'
                ? 'Nenhum alerta ativo'
                : 'Nenhum alerta encontrado'}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {filters.status === 'active'
                ? 'Todos os alertas foram resolvidos'
                : 'Ajuste os filtros para ver mais resultados'}
            </p>
          </div>
        )}

        {/* Detail Modal */}
        <InsightModal
          insightId={selectedAlertId}
          open={!!selectedAlertId}
          onOpenChange={(open) => !open && setSelectedAlertId(null)}
          onCreateEvolution={handleCreateEvolution}
          onScheduleSession={handleScheduleSession}
          onDismiss={handleDismiss}
          isActing={actOnAlert.isPending}
        />

        {/* Dismiss Dropdown */}
        {dismissingAlert && (
          <DismissDropdown
            alert={dismissingAlert}
            open={!!dismissingAlert}
            onOpenChange={(open) => !open && setDismissingAlert(null)}
            onConfirm={(reason, note) => {
              actOnAlert.mutate({
                insightId: dismissingAlert.id,
                actionType: 'dismissed',
                dismissReason: reason,
                note,
              })
              setDismissingAlert(null)
            }}
            isSubmitting={actOnAlert.isPending}
          />
        )}

        {/* Evolution Form Modal */}
        {evolutionAlert && (
          <Dialog open={!!evolutionAlert} onOpenChange={(open) => !open && setEvolutionAlert(null)}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
              <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
                <DialogTitle>Criar Evolução a partir do Alerta</DialogTitle>
                <DialogDescription>
                  Registre a evolução do atendimento com base no alerta clínico.
                </DialogDescription>
              </DialogHeader>
              <RecordForm
                patientId={evolutionAlert.patient_id}
                initialContent={`<p><strong>[Alerta Clínico]</strong> ${evolutionAlert.insight_text}</p>${
                  evolutionAlert.source_report
                    ? `<p><em>Relato original (${new Date(evolutionAlert.source_report.created_at).toLocaleDateString('pt-BR')}):</em> ${evolutionAlert.source_report.content}</p>`
                    : ''
                }`}
                onSuccess={handleEvolutionSaved}
                onCancel={() => setEvolutionAlert(null)}
              />
            </DialogContent>
          </Dialog>
        )}

        {/* Scheduling Modal */}
        {schedulingAlert && (
          <NewAppointmentModal
            open={!!schedulingAlert}
            onOpenChange={(open) => {
              if (!open) {
                // Just close — alert is only marked as resolved if user explicitly confirms
                setSchedulingAlert(null)
              }
            }}
            initialPatientId={schedulingAlert.patient_id}
          />
        )}
      </div>
    </MainLayout>
  )
}

function AlertsSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="card p-4">
          <Skeleton className="h-32 w-full" />
        </div>
      ))}
    </div>
  )
}
